/* Read-only order units beside the native price. No pricing, configuration or cart writes. */
(() => {
  'use strict';
  if (window.KaliaOrderInventory) return;
  const HOSTS = 'kalia-product-studio, kalia-drapery';
  const MOUNT = '[data-kalia-order-inventory]';
  const instances = new Map();
  const text = (value, limit = 240) => typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, limit) : '';
  const positiveQuantity = value => Number.isInteger(value) && value >= 1 && value <= 10 ? value : null;
  const selected = (snapshot, name) => text(snapshot.selections?.find(row => row?.label === name)?.value);
  const hiddenOption = (input, host) => {
    for (let current = input; current && current !== host; current = current.parentElement) {
      if (current.hidden !== true) continue;
      // An inactive legacy step is a view, not a failed dependency rule.
      if (current.matches?.('[data-studio-panel]') && host.dataset?.layout !== 'sequential') continue;
      return true;
    }
    return false;
  };

  // Native dependency rules are authoritative. A hidden or disabled stale radio
  // must never reappear in the summary when a customer changes their motor.
  const shadeSetup = host => {
    const rows = [];
    if (host.ready !== true) return rows;
    host.widgets?.forEach((widget, id) => {
      const field = host.fieldMap?.get(id);
      if (!field || !['mount', 'control', 'operation'].includes(widget.kind) ||
        (typeof host.enabledIds?.has === 'function' && !host.enabledIds.has(id)) ||
        widget.fieldset?.hidden === true || typeof host.fieldVisible !== 'function' || !host.fieldVisible(id)) return;
      const title = text(typeof host.displayTitle === 'function' ? host.displayTitle(field.title) : field.title, 80);
      if (widget.kind === 'operation' && !/^(?:smart control system|eve motionblinds)$/i.test(title)) return;
      const option = typeof host.selectedOption === 'function' ? host.selectedOption(field) : null;
      const item = widget.inputs?.find(value => value?.option?.id === option?.id);
      if (!option || !item?.input?.checked || item.input.disabled || item.input.matches?.(':disabled') ||
        hiddenOption(item.input, host) || host.choices?.[id] !== option.id || !Number.isInteger(option.feeCents) || option.feeCents < 0) return;
      const value = text(typeof host.displayTitle === 'function' ? host.displayTitle(option.label) : option.label);
      if (value) rows.push({ label: title, value });
    });
    // Fixed operations are actual product rules, not inferred from the title.
    if (!rows.some(row => /control|operation/i.test(row.label))) {
      const fixed = text(host.config?.studio?.fixedProperties?.Operation);
      if (fixed) rows.push({ label: 'Operation', value: fixed });
    }
    return rows;
  };
  const sameProduct = (host, snapshot) => {
    const family = host?.localName === 'kalia-drapery' ? 'drapery' : host?.family === 'venetian' ? 'blinds' : 'shades';
    const handle = family === 'drapery' ? host?.dataset?.handle : host?.catalog?.handle;
    return snapshot && snapshot.family === family && snapshot.handle === handle;
  };
  const modelFor = (host, snapshot) => {
    if (!sameProduct(host, snapshot)) return null;
    const drapery = snapshot.family === 'drapery', noun = drapery ? 'panel' : snapshot.family === 'blinds' ? 'blind' : 'shade';
    const quantity = positiveQuantity(snapshot.quantity), width = text(snapshot.enteredMeasurements?.width, 120), height = text(snapshot.enteredMeasurements?.height, 120);
    const rows = [{ label: 'Quantity', value: quantity ? `${quantity} individual ${noun}${quantity === 1 ? '' : 's'}${drapery && quantity === 2 ? ' · one pair' : ''}` : 'Choose a quantity' }, { label: `Size per ${noun}`, value: width && height ? `${width} × ${height}` : `Enter the dimensions for one ${noun} below` }];
    const notes = [];
    if (quantity > 1) notes.push(`The same dimensions and options apply to every ${noun} in this selection.`);
    if (drapery) {
      const heading = selected(snapshot, 'Heading'), lining = selected(snapshot, 'Lining');
      if (heading || lining) rows.push({ label: 'Heading & lining', value: [heading, lining].filter(Boolean).join(' · ') });
      const hardware = host.hardware?.selection;
      if (hardware?.selected === true) {
        const model = selected(snapshot, 'Hardware'), count = selected(snapshot, 'Hardware quantity');
        rows.push({ label: 'Rod / track', value: [model || 'Selected hardware', count && /^\d+$/.test(count) ? `quantity ${count}` : 'review quantity'].join(' · ') });
        notes.push(snapshot.hardwareNeedsReview ? 'Selected hardware still needs its own size, fit and quantity review.' : 'The selected rod or track is a separate item with its own quantity. Review its included components below.');
      } else {
        rows.push({ label: 'Rod / track', value: 'Not selected · panels only' });
        notes.push('Optional rods and tracks are ordered separately. Use your own compatible hardware or add it below.');
      }
    } else {
      const setup = shadeSetup(host);
      if (setup.length) rows.push({ label: 'Mount & control', value: setup.map(row => row.value).join(' · ') });
      notes.push('Selected controls and accessories are listed in Review below.');
    }
    const errors = Array.isArray(snapshot.errors) ? snapshot.errors.map(error => text(typeof error === 'string' ? error : error?.message)).filter(Boolean) : [];
    // A blank measurement is a next step, not an invalid entry. Do not suppress
    // native errors for filled values, unavailable options or missing controls.
    const missingMeasurement = error => {
      const field = /^Enter a (?:finished panel )?(width|height|length) (?:from|in)\b/i.exec(error)?.[1];
      if (field) return field === 'width' ? !width : !height;
      return !width && !height && /^Enter (?:a measurement (?:greater than zero|in cm)|whole inches and choose a fraction)\b/i.test(error);
    };
    const missingOnly = snapshot.valid !== true && errors.length > 0 && errors.every(missingMeasurement);
    const reviewError = errors.find(error => !missingMeasurement(error)) || errors[0];
    const review = !missingOnly && (snapshot.valid !== true || errors.length > 0) ? `Selection needs review${reviewError ? `: ${reviewError}` : '. Complete the required sizes and options below.'}` : '';
    return { title: 'Your selection at a glance', rows, notes, review };
  };
  const node = (tag, className, value) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (value !== undefined) element.textContent = value;
    return element;
  };
  const render = (host, supplied) => {
    const mount = host.querySelector?.(MOUNT);
    if (!mount) return;
    let snapshot = supplied;
    try { if (!snapshot) snapshot = window.KaliaOrderContext?.snapshot?.(); } catch (_) { snapshot = null; }
    const model = modelFor(host, snapshot);
    if (!model) { mount.hidden = true; instances.delete(host); return; }
    const signature = JSON.stringify(model);
    if (instances.get(host) === signature && mount.hidden === false) return;
    const heading = node('h2', 'koi-heading', model.title), list = node('dl', 'koi-list');
    for (const row of model.rows) { const group = node('div'); group.append(node('dt', '', row.label), node('dd', '', row.value)); list.append(group); }
    const fragment = document.createDocumentFragment(); fragment.append(heading, list);
    if (model.review) fragment.append(node('p', 'koi-review', model.review));
    for (const note of model.notes) fragment.append(node('p', 'koi-note', note));
    mount.replaceChildren(fragment); mount.classList.add('koi-inventory'); mount.setAttribute('aria-label', model.title); mount.hidden = false;
    instances.set(host, signature);
  };
  const initialize = (root = document, snapshot) => root.querySelectorAll?.(HOSTS).forEach(host => render(host, snapshot));
  let timer;
  const schedule = event => {
    const host = event?.target?.closest?.(HOSTS);
    if (!host?.querySelector?.(MOUNT)) return;
    clearTimeout(timer); timer = setTimeout(() => render(host), 0);
  };
  document.addEventListener('koa:context-change', event => initialize(document, event.detail?.snapshot));
  document.addEventListener('kalia:product-studio-ready', schedule);
  document.addEventListener('kalia:product-configuration-change', schedule);
  document.addEventListener('input', schedule);
  document.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll?.(HOSTS).forEach(host => instances.delete(host)));
  window.KaliaOrderInventory = Object.freeze({ modelFor, shadeSetup, initialize });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
